import { addDoc, collection, serverTimestamp, type CollectionReference } from 'firebase/firestore';
import type {
  AiVerificationDefaultCandidateCriteria,
  AiVerificationDistribution,
  AiVerificationQuestion,
  AiVerificationRandomConstraints,
  AiVerificationTemplate,
  DistrictMode,
  VerificationTemplatePreset,
} from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * Master verificationTemplatePreset → 地区 aiVerificationTemplates の copy / link 取込
 * hook (Sprint BG-DataSettings 13、GAP-A11 / A-S24 / A-S25)。
 *
 * 旧 v1 `old/apps/admin/src/lib/verification.ts::copyMasterTemplateToDistrict` /
 * `linkMasterTemplateToDistrict` を踏襲。Survey 版 (`useSurveyCopyLink`) と pattern を合わせる。
 *
 * - **copy**: preset.template の questions / displayName / description / targetMode / defaults を
 *   deep clone し、独立した AiVerificationTemplate を地区 subcollection に新規 doc 作成。
 *   以後 preset 側の変更は反映されない (sourceMasterTemplateId はトレース用に保持)。
 *   linkedToMaster = 'forked'、status は 'draft' に強制 (admin が地区側で公開判断)。
 * - **link**: 同じく地区 subcollection に新規作成するが、linkedToMaster = 'linked' +
 *   masterVersion を保持することで「再リンク (refresh)」候補と認識される (再リンク UI は別 sprint)。
 *
 * いずれも新規 docId は addDoc auto-id (旧 v1 では generateTemplateId)、戻り値は新規 templateId。
 *
 * 重要: M9 preset の本体 schema は `template: unknown` (forward compatible)。本 hook は
 * 内部の visual editor が書き込む shape (`PresetTemplateBody`) を前提に動作するが、object
 * でない / 必要 field がない場合はデフォルト値で fallback する (旧 v1 互換)。
 */

/**
 * preset.template に格納される構造。VerificationTemplatePresetEditPage (visual editor) が
 * この shape で書き込む。AiVerificationTemplate と互換 (一部 optional)。
 *
 * 旧 v1 master template は AiVerificationTemplate そのものを保存していたが、v2 の M9
 * preset は MasterCommon + mode 外側 + `template` 内側、という 2 層構造を採る (docs/04 §3-1)。
 */
export interface PresetTemplateBody {
  /** preset の displayName を 地区 template に持ち越す (空なら preset.displayName fallback)。 */
  displayName?: string;
  description?: string;
  /** preset.template.targetMode は preset.mode と独立 (旧 v1 の AiVerificationTemplate.targetMode 踏襲)。 */
  targetMode?: DistrictMode | null;
  perPostQuestions?: AiVerificationQuestion[];
  perClassificationQuestions?: AiVerificationQuestion[];
  allowFreeComment?: boolean;
  defaultDistribution?: AiVerificationDistribution;
  defaultRandomConstraints?: AiVerificationRandomConstraints;
  defaultCandidateCriteria?: AiVerificationDefaultCandidateCriteria;
  defaultEndAtOffsetDays?: number;
  classificationId?: string | null;
}

/** AiVerificationQuestion[] を deep clone — 旧 cloneQuestions 踏襲。 */
export function cloneVerificationQuestions(
  questions: AiVerificationQuestion[],
): AiVerificationQuestion[] {
  if (typeof structuredClone === 'function') {
    return structuredClone(questions);
  }
  return JSON.parse(JSON.stringify(questions)) as AiVerificationQuestion[];
}

/**
 * preset.template (unknown) を安全に PresetTemplateBody に narrow。
 * object でない / 配列 / null は空 body 扱い (旧 v1 互換)。
 */
export function readPresetTemplate(preset: VerificationTemplatePreset): PresetTemplateBody {
  const t = preset.template;
  if (!t || typeof t !== 'object' || Array.isArray(t)) return {};
  const body = t as Record<string, unknown>;
  const out: PresetTemplateBody = {};
  if (typeof body.displayName === 'string') out.displayName = body.displayName;
  if (typeof body.description === 'string') out.description = body.description;
  // MD-3 follow-up (モードドリブン化 2026-07-24): 旧組込4種 whitelist を撤廃 — custom mode の
  // targetMode が preset→地区 copy/link で黙って失われるバグ修正。非空 string / null を受理。
  if (typeof body.targetMode === 'string' && body.targetMode.trim()) {
    out.targetMode = body.targetMode.trim();
  } else if (body.targetMode === null) {
    out.targetMode = null;
  }
  if (Array.isArray(body.perPostQuestions)) {
    out.perPostQuestions = body.perPostQuestions.filter(
      (q): q is AiVerificationQuestion =>
        typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
    );
  }
  if (Array.isArray(body.perClassificationQuestions)) {
    out.perClassificationQuestions = body.perClassificationQuestions.filter(
      (q): q is AiVerificationQuestion =>
        typeof q === 'object' && q !== null && typeof (q as { id?: unknown }).id === 'string',
    );
  }
  if (typeof body.allowFreeComment === 'boolean') out.allowFreeComment = body.allowFreeComment;
  if (
    body.defaultDistribution === 'all_same' ||
    body.defaultDistribution === 'random_per_user' ||
    body.defaultDistribution === 'manual' ||
    body.defaultDistribution === 'free'
  ) {
    out.defaultDistribution = body.defaultDistribution;
  }
  if (body.defaultRandomConstraints && typeof body.defaultRandomConstraints === 'object') {
    out.defaultRandomConstraints = body.defaultRandomConstraints as AiVerificationRandomConstraints;
  }
  if (body.defaultCandidateCriteria && typeof body.defaultCandidateCriteria === 'object') {
    out.defaultCandidateCriteria =
      body.defaultCandidateCriteria as AiVerificationDefaultCandidateCriteria;
  }
  if (typeof body.defaultEndAtOffsetDays === 'number') {
    out.defaultEndAtOffsetDays = body.defaultEndAtOffsetDays;
  }
  if (typeof body.classificationId === 'string' || body.classificationId === null) {
    out.classificationId = body.classificationId as string | null;
  }
  return out;
}

/** preset.template.displayName が空のとき、master preset の displayName を fallback として使う。 */
export function resolvePresetDisplayName(
  preset: Pick<VerificationTemplatePreset, 'displayName'>,
  body: PresetTemplateBody,
): string {
  const t = body.displayName?.trim();
  return t ? t : preset.displayName;
}

interface CommonImportArgs {
  districtId: string;
  preset: VerificationTemplatePreset & { id: string };
  actorUid: string;
  /** test mock 用 (default は districts/{id}/aiVerificationTemplates)。 */
  collectionRef?: CollectionReference;
}

/**
 * 共通 payload 構築 (copy / link で linkedToMaster だけ差し替えるための helper)。
 */
function buildImportPayload(
  args: CommonImportArgs,
  linkedToMaster: 'forked' | 'linked',
): Record<string, unknown> {
  const body = readPresetTemplate(args.preset);
  const payload: Record<string, unknown> = {
    displayName: resolvePresetDisplayName(args.preset, body),
    description: body.description ?? '',
    targetMode: body.targetMode ?? null,
    perPostQuestions: cloneVerificationQuestions(body.perPostQuestions ?? []),
    perClassificationQuestions: cloneVerificationQuestions(body.perClassificationQuestions ?? []),
    allowFreeComment: typeof body.allowFreeComment === 'boolean' ? body.allowFreeComment : true,
    status: 'draft' as AiVerificationTemplate['status'],
    version: 1,
    sourceMasterTemplateId: args.preset.id,
    linkedToMaster,
    masterVersion: args.preset.version,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: args.actorUid,
    updatedBy: args.actorUid,
  };
  if (body.defaultDistribution !== undefined)
    payload.defaultDistribution = body.defaultDistribution;
  if (body.defaultRandomConstraints !== undefined) {
    payload.defaultRandomConstraints = body.defaultRandomConstraints;
  }
  if (body.defaultCandidateCriteria !== undefined) {
    payload.defaultCandidateCriteria = body.defaultCandidateCriteria;
  }
  if (typeof body.defaultEndAtOffsetDays === 'number') {
    payload.defaultEndAtOffsetDays = body.defaultEndAtOffsetDays;
  }
  if (body.classificationId !== undefined) payload.classificationId = body.classificationId;
  return payload;
}

/**
 * preset を copy で地区 template 化する (forked、以後独立)。
 * 戻り値: 新規 template の id。
 */
export async function copyPresetToDistrict(
  districtId: string,
  preset: VerificationTemplatePreset & { id: string },
  actorUid: string,
  collectionRef?: CollectionReference,
): Promise<string> {
  const ref = collectionRef ?? collection(db, 'districts', districtId, 'aiVerificationTemplates');
  const payload = buildImportPayload({ districtId, preset, actorUid, collectionRef }, 'forked');
  const created = await addDoc(ref, payload);
  return created.id;
}

/**
 * preset を link で地区 template 化する (linked、再リンクで master 最新化候補)。
 * 戻り値: 新規 template の id。
 *
 * 旧 v1 と同様、template doc は新規作成し、`linkedToMaster: 'linked'` + `masterVersion` を保持。
 * 「再リンク」UI (linked template に対する preset 最新版での再 sync) は別 sprint。
 */
export async function linkPresetToDistrict(
  districtId: string,
  preset: VerificationTemplatePreset & { id: string },
  actorUid: string,
  collectionRef?: CollectionReference,
): Promise<string> {
  const ref = collectionRef ?? collection(db, 'districts', districtId, 'aiVerificationTemplates');
  const payload = buildImportPayload({ districtId, preset, actorUid, collectionRef }, 'linked');
  const created = await addDoc(ref, payload);
  return created.id;
}

/**
 * 取込候補 preset を絞り込む helper:
 *   - archivedAt が null
 *   - enabled === true
 *   - template.status === 'published' (status 未設定の preset は許可 ─ 旧 v1 互換)
 *
 * UI から filter として呼び出される (Modal 側で適用)。
 */
export function selectImportablePresets(
  presets: Array<VerificationTemplatePreset & { id: string }>,
): Array<VerificationTemplatePreset & { id: string }> {
  return presets.filter((p) => {
    if (p.archivedAt) return false;
    if (p.enabled === false) return false;
    const body = readPresetTemplate(p);
    // 旧 v1 互換: status 未指定 (template 自体が unknown / object でない) は通す。
    // ただし visual editor 経由で status が書き込まれている場合は 'published' のみ通す。
    const rawTemplate = p.template;
    if (rawTemplate && typeof rawTemplate === 'object' && !Array.isArray(rawTemplate)) {
      const status = (rawTemplate as { status?: unknown }).status;
      if (status === 'draft' || status === 'archived') return false;
    }
    // body は dead-store にしないため簡易参照 (linter 抑止)。
    void body;
    return true;
  });
}
